import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, formatApiError } from "../lib/api";
import { ShieldCheck, Check, X } from "lucide-react";
import { toast } from "sonner";

const typeLabel = { transaction: "Cash book entry", invoice: "Invoice", voucher: "Voucher / Receipt", master: "Account / party / employee" };

export default function Approvals() {
  const [reqs, setReqs] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    try { setReqs((await api.get("/deletion-requests")).data); }
    catch { setReqs([]); toast.error("Only admin can review approvals", { id: "approvals-403" }); return; }
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = reqs.length > 0 && reqs.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(reqs.map((r) => r.id)));

  const act = async (r, action) => {
    const verb = action === "approve" ? (r.action === "edit" ? "Approve this edit" : "Approve and delete") : "Reject";
    if (!window.confirm(`${verb} ${r.label}?`)) return;
    try {
      await api.post(`/deletion-requests/${r.id}/${action}`);
      toast.success(action === "approve" ? (r.action === "edit" ? "Edit approved" : "Deleted") : "Request rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkAct = async (action) => {
    const ids = reqs.filter((r) => selected.has(r.id)).map((r) => r.id);
    if (ids.length === 0) return toast.error("Select at least one request");
    const verb = action === "approve" ? "Approve & delete" : "Reject";
    if (!window.confirm(`${verb} ${ids.length} selected request(s)?`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.post(`/deletion-requests/${id}/${action}`); ok++; }
      catch { fail++; }
    }
    toast[fail ? "info" : "success"](`${ok} ${action === "approve" ? "approved" : "rejected"}${fail ? `, ${fail} failed` : ""}`);
    load();
  };

  return (
    <Layout>
      <div className="p-8 max-w-4xl mx-auto">
        <PageHeader title="Approvals" subtitle="Edit and delete requests (with reasons) waiting for admin review — act one by one or in bulk." />

        {reqs.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground" data-testid="approvals-empty">
            <ShieldCheck className="mx-auto mb-3 opacity-40" size={32} /> No pending requests. All clear.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="approval-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                Select all
              </label>
              {selected.size > 0 && (
                <div data-testid="approval-bulk-bar" className="flex flex-wrap items-center gap-2 ml-auto">
                  <span className="text-sm font-medium">{selected.size} selected</span>
                  <button data-testid="approval-bulk-approve" onClick={() => bulkAct("approve")}
                    className="h-9 px-3 rounded-md bg-destructive text-destructive-foreground text-sm flex items-center gap-1.5 hover:bg-destructive/90 transition-colors">
                    <Check size={14} /> Approve selected
                  </button>
                  <button data-testid="approval-bulk-reject" onClick={() => bulkAct("reject")}
                    className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
                    <X size={14} /> Reject selected
                  </button>
                  <button data-testid="approval-bulk-clear" onClick={() => setSelected(new Set())}
                    className="h-9 px-3 rounded-md border border-input bg-card text-sm hover:bg-muted transition-colors">Clear</button>
                </div>
              )}
            </div>
            <div className="space-y-3 animate-fade-up">
              {reqs.map((r) => (
                <div key={r.id} data-testid={`approval-${r.id}`} className={`rounded-xl border bg-card p-4 flex flex-wrap items-center justify-between gap-3 ${selected.has(r.id) ? "border-accent/40 bg-accent/5" : "border-border"}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <input data-testid={`approval-select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{typeLabel[r.entity_type] || r.entity_type}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${r.action === "edit" ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>{r.action === "edit" ? "Edit" : "Delete"}</span>
                        <span className="font-medium truncate">{r.label}</span>
                        {r.entity_type !== "master" && <span className="num font-bold">{inr(r.amount)}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 num">
                        {r.entry_date ? `${r.entry_date} · ` : ""}Requested by {r.requested_by_name}
                        {r.reason ? ` · "${r.reason}"` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`approve-${r.id}`} onClick={() => act(r, "approve")}
                      className="h-9 px-3 rounded-md bg-destructive text-destructive-foreground text-sm flex items-center gap-1.5 hover:bg-destructive/90 transition-colors">
                      <Check size={14} /> {r.action === "edit" ? "Approve edit" : "Approve delete"}
                    </button>
                    <button data-testid={`reject-${r.id}`} onClick={() => act(r, "reject")}
                      className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
                      <X size={14} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
